import { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAlert } from '../../../constants/AlertProvider';
import { ApiService } from "../../../api/services.gen";
import axios from "axios";

const SkillLevelModal = ({ skill, onSave, onCancel, isLoading }) => {
    const [level, setLevel] = useState('Intermediate');

    const levels = {
        'Beginner': 2,
        'Intermediate': 4,
        'Advanced': 6,
        'Expert': 8,
        'Master': 10
    };

    const handleSave = () => {
        onSave(levels[level]);
    };

    return (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 p-6 shadow-2xl">
                {/* Header */}
                <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                            Skill level
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Set your level for{" "}
                            <span className="text-blue-600">"{skill.name}"</span>
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Choose the option that best matches your current proficiency.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-full p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition"
                    >
                        <span className="sr-only">Close</span>
                        ✕
                    </button>
                </div>

                {/* Levels */}
                <div className="mb-6 space-y-2">
                    {Object.keys(levels).map((levelName) => (
                        <label
                            key={levelName}
                            className={`flex items-center justify-between rounded-lg border p-3 text-sm cursor-pointer transition
            ${level === levelName
                                    ? "border-blue-600 bg-blue-50/70"
                                    : "border-gray-200 hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                {/* Custom radio bullet */}
                                <span
                                    className={`flex h-4 w-4 items-center justify-center rounded-full border
                ${level === levelName
                                            ? "border-blue-600 bg-blue-600"
                                            : "border-gray-300 bg-white"
                                        }`}
                                >
                                    {level === levelName && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                    )}
                                </span>

                                <span className="font-medium text-gray-800 dark:text-gray-200">{levelName}</span>
                            </div>

                            <input
                                type="radio"
                                name="skillLevel"
                                value={levelName}
                                checked={level === levelName}
                                onChange={() => setLevel(levelName)}
                                className="sr-only"
                            />
                        </label>
                    ))}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isLoading}
                        className="rounded-md border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isLoading}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>

    );
};

export function AddSkills({ applicationId }) {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // To manage loading state for add/remove
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [skillToAdd, setSkillToAdd] = useState(null);
    const showAlert = useAlert();

    useEffect(() => {
        if (!applicationId) return;

        const fetchExistingSkills = async () => {
            try {
                const skiResponse = await axios.get(`/api/skill/?application_id=${applicationId}`, { withCredentials: true });
                if (skiResponse.data && Array.isArray(skiResponse.data.results)) {
                    setSelectedSkills(skiResponse.data.results);
                }
            } catch (err) {
                console.error("Error fetching existing skills:", err);
                showAlert("Failed to load existing skills", "error");
            }
        };

        fetchExistingSkills();
    }, [applicationId]);

    useEffect(() => {
        const fetchSkills = async () => {
            if (query.trim().length < 2) {
                setSuggestions([]);
                return;
            }
            try {
                const res = await axios.get(`/api/matching-skills/autocomplete/?q=${query}`);
                console.log("Fetching Suggs...", res.data.skills); //debug
                setSuggestions(res.data.skills);
            } catch (err) {
                console.error("Error fetching skills:", err);
            }
        };
        const delayDebounce = setTimeout(fetchSkills, 300);
        return () => clearTimeout(delayDebounce);
    }, [query]);

    const handleSelectSkill = (skillObj) => {
        if (isLoading) return;
        // Prevent duplicates
        if (selectedSkills.some(s => s.name.toLowerCase() === skillObj.name.toLowerCase())) {
            setQuery("");
            setSuggestions([]);
            return;
        }
        setSkillToAdd(skillObj);
        setIsModalOpen(true);
    };

    const saveSkillWithLevel = async (score) => {
        if (!skillToAdd) return;

        setIsLoading(true);
        try {
            // Create the new skill and get its response to add to state
            const newSkill = await ApiService.apiSkillCreate({
                requestBody: {
                    application: applicationId,
                    name: skillToAdd.name,
                    score: score,
                    matching_skill_id: skillToAdd.id || null,
                }
            });
            // Add the newly created skill to our local state
            setSelectedSkills(prevSkills => [...prevSkills, newSkill]);
            showAlert("Skill added!", "success");
        } catch (err) {
            showAlert("Failed to add skill", "error");
            console.error("Failed to add skill:", err);
        } finally {
            setIsLoading(false);
        }
        setIsModalOpen(false);
        setSkillToAdd(null);
        setQuery("");
        setSuggestions([]);
    };

    const removeSkill = async (skillToRemove) => {
        if (isLoading) return; // Prevent action if already loading
        setIsLoading(true);
        try {
            await ApiService.apiSkillDestroy({ id: skillToRemove.id.toString() });
            setSelectedSkills(selectedSkills.filter((s) => s.id !== skillToRemove.id));
            showAlert("Skill removed!", "success");
        } catch (err) {
            showAlert("Failed to remove skill", "error");
            console.error("Failed to remove skill:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && query.trim() !== "") {
            e.preventDefault();
            handleSelectSkill({ name: query.trim(), id: null });
        }
    };

    return (
        <div className="w-full flex flex-col items-center gap-y-4">
            <div className="w-full relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                    type="text"
                    id="skill"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-10 pr-4 h-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                    placeholder="Search..."
                    disabled={isLoading}
                    required
                />
                {suggestions.length > 0 && (
                    <ul className="absolute top-full mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto z-10">
                        {suggestions.map((skill, idx) => (
                            <li
                                key={skill.id || idx}
                                // Prevent adding skill if already loading
                                onClick={() => !isLoading && handleSelectSkill(skill)}
                                className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-[14px] text-gray-900 dark:text-gray-100 ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                {skill.name}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className="w-full flex flex-wrap gap-2">
                {selectedSkills.map((skill) => (
                    <div
                        key={skill.id}
                        className="p-2 h-8 border-2 border-blue-700 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30 flex flex-row gap-x-2 items-center rounded-lg"
                    >
                        <span className="text-blue-700 dark:text-blue-300 text-[14px] font-medium">{skill.name}</span>
                        <button type="button" onClick={() => removeSkill(skill)} disabled={isLoading}>
                            <XMarkIcon className="size-4 text-blue-700/50 cursor-pointer" />
                        </button>
                    </div>
                ))}
            </div>
            {isModalOpen && skillToAdd && (
                <SkillLevelModal
                    skill={skillToAdd}
                    onSave={saveSkillWithLevel}
                    onCancel={() => { setIsModalOpen(false); setSkillToAdd(null); }}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
}